import {
  MessageSquare,
  FileText,
  ReceiptText,
  Handshake,
  Landmark,
  FolderKanban,
  Bell,
} from "lucide-react";

export function getNotificationIcon(type) {
  const normalizedType = String(type || "").toUpperCase();

  if (normalizedType.startsWith("MESSAGE")) {
    return MessageSquare;
  }

  if (normalizedType.startsWith("DOCUMENT")) {
    return FileText;
  }

  if (normalizedType.startsWith("INVOICE")) {
    return ReceiptText;
  }

  if (normalizedType.startsWith("OSS")) {
    return Handshake;
  }

  if (
    normalizedType.startsWith("LOAN") ||
    normalizedType.startsWith("LENDER")
  ) {
    return Landmark;
  }

  if (
    normalizedType.startsWith("DOSSIER") ||
    normalizedType.startsWith("RENOVATION") ||
    normalizedType.startsWith("PROJECT")
  ) {
    return FolderKanban;
  }

  return Bell;
}

export function getNotificationSeverityClass(severity) {
  const severityClasses = {
    SUCCESS: "is-success",
    WARNING: "is-warning",
    ERROR: "is-error",
    INFO: "is-info",
  };

  const normalizedSeverity = String(severity || "INFO").toUpperCase();

  return severityClasses[normalizedSeverity] || severityClasses.INFO;
}

export function formatNotificationTime(value, t) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const differenceInSeconds = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / 1000),
  );

  if (differenceInSeconds < 45) {
    return t("time.justNow", "Just now");
  }

  const differenceInMinutes = Math.floor(differenceInSeconds / 60);

  if (differenceInMinutes < 60) {
    if (differenceInMinutes === 1) {
      return t("time.minuteAgo", "1 minute ago");
    }

    return t("time.minutesAgo", {
      count: differenceInMinutes,
      defaultValue: "{{count}} minutes ago",
    });
  }

  const differenceInHours = Math.floor(differenceInMinutes / 60);

  if (differenceInHours < 24) {
    if (differenceInHours === 1) {
      return t("time.hourAgo", "1 hour ago");
    }

    return t("time.hoursAgo", {
      count: differenceInHours,
      defaultValue: "{{count}} hours ago",
    });
  }

  const differenceInDays = Math.floor(differenceInHours / 24);

  if (differenceInDays < 7) {
    if (differenceInDays === 1) {
      return t("time.dayAgo", "1 day ago");
    }

    return t("time.daysAgo", {
      count: differenceInDays,
      defaultValue: "{{count}} days ago",
    });
  }

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function firstText(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function buildNotificationMeta(notification, metadata, t) {
  const type = String(notification?.notification_type || "").toUpperCase();
  const result = [];
  const push = (key, label, value) => {
    if (value) result.push({ key, label, value: String(value) });
  };

  const project = firstText(
    metadata.project_code,
    metadata.project_label,
    metadata.project_title,
    notification?.project_code,
  );
  const loanCase = firstText(
    metadata.loan_case_reference,
    metadata.case_reference,
    notification?.loan_case_reference,
  );
  const dossier = firstText(metadata.dossier_code, notification?.dossier_code);

  // An organisation that sent an event is not necessarily a lender.
  // Only label it "Lender" when the event or metadata explicitly identifies it.
  const lender = firstText(
    metadata.lender_name,
    type.startsWith("LOAN") || type.startsWith("LENDER")
      ? metadata.organisation_name || notification?.actor_organisation_name
      : "",
  );

  push("project", t("labels.project", "Project"), project);
  push("loan-case", t("labels.loanCase", "Loan case"), loanCase);
  push("dossier", t("labels.dossier", "Dossier"), dossier);
  push("lender", t("labels.lender", "Lender"), lender);

  if (type.startsWith("INVOICE")) {
    const invoiceId = firstText(metadata.invoice_id, notification?.invoice_id);
    const version = firstText(metadata.version_number);
    push("invoice", t("labels.invoice", "Invoice"), invoiceId ? `#${invoiceId}` : "");
    push("version", t("labels.version", "Version"), version);
  }

  return result;
}

export function getNotificationContent(notification, t) {
  const notificationType = String(
    notification?.notification_type || "",
  ).toUpperCase();

  const metadata =
    notification?.extra_metadata &&
    typeof notification.extra_metadata === "object" &&
    !Array.isArray(notification.extra_metadata)
      ? notification.extra_metadata
      : {};

  const fallbackTitle =
    notification?.title || t("fallback.title", "Notification");

  const fallbackBody = notification?.body || "";

  const time = formatNotificationTime(notification?.created_at, t);

  const meta = buildNotificationMeta(notification, metadata, t);

  // =========================================================
  // GENERIC / UNKNOWN TYPE
  // =========================================================

  if (!notificationType) {
    return {
      title: fallbackTitle,
      body: fallbackBody,
      context: "",
      preview: "",
      meta,
      time,
    };
  }

  // =========================================================
  // MESSAGE RECEIVED
  // =========================================================

  if (notificationType === "MESSAGE_RECEIVED") {
    const senderName =
      metadata.sender_name ||
      notification?.actor_organisation_name ||
      notification?.actor_display_name ||
      notification?.actor_name ||
      t("fallback.sender", "A platform user");

    const threadTitle =
      metadata.thread_title || t("fallback.conversation", "Conversation");

    const messagePreview = metadata.message_preview || notification?.body || "";

    return {
      title: t("events.MESSAGE_RECEIVED.title", {
        sender_name: senderName,
        defaultValue: "New message from {{sender_name}}",
      }),

      // The thread label and preview already convey the message content.
      // Avoid repeating the same preview as a second paragraph.
      body: "",

      context: threadTitle,
      preview: messagePreview,
      meta: [],
      time,
    };
  }

  // =========================================================
  // LENDER CONNECTED
  // =========================================================

  if (notificationType === "LENDER_CONNECTED") {
    const lenderName =
      metadata.lender_name ||
      notification?.actor_organisation_name ||
      t("fallback.lender", "A participating lender");

    return {
      title: t("events.LENDER_CONNECTED.title", {
        lender_name: lenderName,
        defaultValue: "Lender connected to your dossier",
      }),

      body: fallbackBody || t("events.LENDER_CONNECTED.body", {
        lender_name: lenderName,
        dossier_code: metadata.dossier_code || "",
        case_reference: metadata.case_reference || "",
        defaultValue:
          "{{lender_name}} connected to your dossier and created a loan case.",
      }),

      context: "",
      preview: "",
      meta,
      time,
    };
  }

  // =========================================================
  // ALL OTHER NOTIFICATION TYPES
  // =========================================================

  const translationBase = `events.${notificationType}`;

  return {
    // Trust the backend's event-specific text when present. For example,
    // an invoice replacement may still use type INVOICE_UPLOADED for legacy
    // compatibility, but its backend title correctly says "updated".
    title: notification?.title || t(`${translationBase}.title`, {
      ...metadata,
      defaultValue: fallbackTitle,
    }),

    body: notification?.body || t(`${translationBase}.body`, {
      ...metadata,
      defaultValue: fallbackBody,
    }),

    context: "",
    preview: "",
    meta,
    time,
  };
}
